import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { X, Loader2, AlertCircle, ImagePlus } from 'lucide-react';
import { toast } from 'sonner';
import ConstructionContextSelector from './ConstructionContextSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const photoUrl = (s3Key) => `${API}/files/serve/${s3Key}`;

// Build a deviation-focused title/description from a flagged job.
export const deviationPrefill = (job) => {
  const dev = (job?.result || {}).deviation || {};
  const type = job?.type || 'Analysis';
  const label = type === 'MEASURE_2D' || type === 'MEASURE_3D' ? 'Measurement' : 'Count';
  const sign = dev.percent > 0 ? '+' : '';
  return {
    title: `Deviation flagged: ${job?.roomName || (job?.result || {}).roomName || type} (${sign}${dev.percent}%)`,
    description: `${label} deviation exceeds tolerance.\nExpected: ${dev.expected}\nActual: ${dev.actual}\nVariance: ${sign}${dev.percent}% (tolerance ${dev.tolerance}%)\n\nPlease investigate and resolve.`,
  };
};

// Fetch the source image of a job as an attachment {s3Key, fileName} (or null).
export const sourceImageAttachment = async (projectId, uploadId) => {
  if (!uploadId) return null;
  try {
    const res = await axios.get(`${API}/uploads/${uploadId}/signed-url`);
    const url = res.data.url || '';
    const marker = '/api/files/serve/';
    const idx = url.indexOf(marker);
    if (idx === -1) return null;
    const s3Key = url.slice(idx + marker.length);
    return { s3Key, fileName: s3Key.split('/').pop() };
  } catch (e) {
    return null;
  }
};

// Derive a sensible pre-filled title/description from a completed analysis job.
export const prefillFromJob = (job) => {
  if (!job) return { title: '', description: '' };
  const r = job.result || {};
  const s = r.summary || {};
  const type = job.type || 'Analysis';
  let title = `Follow-up: ${type}`;
  const parts = [];
  if (s.objectType) parts.push(`Object type: ${s.objectType}`);
  if (s.totalCount != null) parts.push(`Detected count: ${s.totalCount}`);
  if (s.qualityScore != null) parts.push(`Quality score: ${Math.round(s.qualityScore * 100)}%`);
  const recon = r.reconciliation;
  if (recon && recon.expectedQty != null) {
    parts.push(`Expected ${recon.expectedQty}, variance ${recon.variance} (${recon.variancePercent?.toFixed?.(1) ?? recon.variancePercent}%)`);
    if (recon.variance && Number(recon.variance) !== 0) title = `Count mismatch: ${type}`;
  }
  const qIssues = (s.issues || []).filter((i) => i.percent > 0);
  if (qIssues.length) {
    title = `Quality issue: ${s.objectType || type}`;
    parts.push(`Issues: ${qIssues.map((i) => `${i.type.replace('_', ' ')} ${i.percent}%`).join(', ')}`);
  }
  if (r.roomName || s.roomName) parts.push(`Location: ${r.roomName || s.roomName}`);
  return { title, description: parts.join('\n') };
};

const CreateIssueModal = ({ projectId, job, open, onClose, onCreated, titleOverride, descriptionOverride, initialAssignee, initialAttachments }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [priority, setPriority] = useState('medium');
  const [team, setTeam] = useState([]);
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [constructionCtx, setConstructionCtx] = useState({});
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const pre = prefillFromJob(job);
    setTitle(titleOverride || pre.title);
    setDescription(descriptionOverride != null ? descriptionOverride : pre.description);
    setAssignedTo(initialAssignee || '');
    setDueDate('');
    setPriority('medium');
    setAttachments(initialAttachments || []);
    setConstructionCtx(job?.constructionContext || {});
    axios.get(`${API}/team`).then((r) => setTeam(r.data || [])).catch(() => setTeam([]));
  }, [open, job, titleOverride, descriptionOverride, initialAssignee, initialAttachments]);

  if (!open) return null;

  const uploadPhoto = async (e) => {
    const file = (e.target.files || [])[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/projects/${projectId}/issue-photos`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAttachments((prev) => [...prev, res.data]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Photo upload failed');
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/projects/${projectId}/issues`, {
        title: title.trim(),
        description,
        sourceJobId: job?.id || null,
        assignedTo: assignedTo || null,
        dueDate: dueDate || null,
        priority,
        attachments,
        constructionContext: Object.keys(constructionCtx || {}).length ? constructionCtx : null,
      });
      toast.success('Issue created');
      onCreated && onCreated(res.data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create issue');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="create-issue-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-500" /> Create Issue
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="create-issue-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {job && (
            <p className="text-xs text-slate-500 bg-slate-50 rounded p-2">Linked to analysis job <span className="font-mono">{job.id.slice(0, 8)}</span> ({job.type})</p>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg" data-testid="issue-title-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm" data-testid="issue-desc-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Assign To</label>
              <select value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg" data-testid="issue-assignee-select">
                <option value="">Unassigned</option>
                {assignedTo && !team.find((u) => u.id === assignedTo) && (
                  <option value={assignedTo}>{job?.createdByName || 'Job creator'}</option>
                )}
                {team.map((u) => <option key={u.id} value={u.id}>{u.name || u.email} ({u.role})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
              <select value={priority} onChange={(e) => setPriority(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg" data-testid="issue-priority-select">
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-lg" data-testid="issue-due-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Construction Context {job?.constructionContext ? <span className="text-xs text-slate-400">(inherited from source — editable)</span> : ''}</label>
            <ConstructionContextSelector projectId={projectId} value={constructionCtx} onChange={setConstructionCtx} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Photo Evidence</label>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="issue-photo-input" />
            <div className="flex items-center gap-2 flex-wrap">
              {attachments.map((a, i) => (
                <img key={i} src={photoUrl(a.s3Key)} alt={a.fileName} className="w-16 h-16 object-cover rounded-lg border border-slate-200" data-testid="issue-photo-thumb" />
              ))}
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-16 h-16 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-50 disabled:opacity-60" data-testid="issue-add-photo-btn">
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-5 py-4 border-t border-slate-200">
          <button onClick={onClose} className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200">Cancel</button>
          <button onClick={submit} disabled={saving} className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-60 flex items-center gap-2" data-testid="issue-submit-btn">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Create Issue
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateIssueModal;
